import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Upload, Table, Space, Tooltip, Input, Select, Row, Col, Typography, Card, message, Form, Modal, Alert, Spin, Divider } from 'antd';
import { InboxOutlined, ExclamationCircleOutlined, FileTextOutlined, InfoCircleFilled, ClockCircleFilled, CheckCircleFilled, WarningFilled, BulbFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import WizardProgress from './WizardProgress';
import { upload_file, get_cycle_loads, estimate_file_time } from '../../redux/actions/selfActions';
import { get_cycle_status_desc } from '../../utils/get_status_desc';

const { Dragger } = Upload;
const { Option } = Select;

const WizardUpload = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	// Ref to track if we've already fetched loads for current cycle
	const fetchedCycleRef = React.useRef(null);
	// Ref to track previous cycle ID to detect real changes
	const previousCycleIdRef = React.useRef(null);

	// Helper function to get load type value for API
	const getLoadTypeValue = (loadType) => {
		switch (loadType) {
			case 'Inicial': return 1;
			case 'Adicional': return 2;
			case 'Reestructura': return 3;
			case 'Coordenadas': return 4;
			default: return 1;
		}
	};

	const [uploadType, setUploadType] = useState('Inicial');
	const [loadName, setLoadName] = useState('');
	const [loadDescription, setLoadDescription] = useState('');
	const [fileName, setFileName] = useState('');
	const [fileDescription, setFileDescription] = useState('');
	const [selectedFile, setSelectedFile] = useState(null);
	const [filePreview, setFilePreview] = useState(null);
	const [uploads, setUploads] = useState([]);
	const [viewErrorDetails, setViewErrorDetails] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [estimationModal, setEstimationModal] = useState(false);
	const [timeEstimation, setTimeEstimation] = useState(null);
	const [isEstimating, setIsEstimating] = useState(false);
	const { self_errors, cycle_loads_loading, cycle_loads } = useSelector(state => state.self);

	// Get wizard state from localStorage
	const [wizardState, setWizardState] = useState(() => JSON.parse(localStorage.getItem('wizardState') || '{}'));

	// Determine available load types based on cycle selection and cycle status
	const getAvailableLoadTypes = () => {
		// cycleSelectionType is now a number representing the cycle status
		const cycleStatus = wizardState.cycleSelectionType;

		if (cycleStatus === 1) {
			// Status 1: cycle can only have initial load
			return ['Inicial'];
		} else if (cycleStatus === 3) {
			// Status 3: cycle cannot have initial load, only additional types
			return ['Reestructura', 'Coordenadas'];
		} else {
			// Default for other statuses - no initial load
			return ['Reestructura', 'Coordenadas'];
		}
	};

	const availableLoadTypes = getAvailableLoadTypes();
	const isLoadTypeDisabled = availableLoadTypes.length === 1;

	// Get cycle load data from Redux
	const cycleLoad = cycle_loads.loads && cycle_loads.loads.length > 0 ? cycle_loads.loads[0] : null;
	const cycleLoadError = cycle_loads.count === 0 ? 'Este ciclo no tiene una carga asociada' : null;

	useEffect(() => {
		// Set default load name based on cycle and load type
		if (wizardState.cycle) {
			// Ensure the current uploadType is available, otherwise set to first available
			if (!availableLoadTypes.includes(uploadType)) {
				setUploadType(availableLoadTypes[0]);
			}
			
			const defaultName = `Carga ${uploadType} ${wizardState.cycle.name}`;
			setLoadName(defaultName);
		}
	}, [wizardState.cycle, wizardState.loadType, availableLoadTypes, uploadType]);

	// Fetch cycle loads for Initial upload type
	useEffect(() => {
		if (uploadType === 'Inicial' && wizardState.cycle) {
			const cycleId = wizardState.cycle._id?.$oid || wizardState.cycle._id || wizardState.cycle.id;

			// Only fetch if we haven't already fetched for this cycle
			if (cycleId && fetchedCycleRef.current !== cycleId) {
				fetchedCycleRef.current = cycleId;
				dispatch(get_cycle_loads(cycleId));
			}
		}
	}, [uploadType, wizardState.cycle, dispatch]);

	// Update wizard state when cycle load is found
	useEffect(() => {
		if (cycleLoad && !wizardState.load_id) {
			const updatedWizardState = {
				...wizardState,
				load_id: cycleLoad._id.$oid
			};
			setWizardState(updatedWizardState);
			localStorage.setItem('wizardState', JSON.stringify(updatedWizardState));
		}
	}, [cycleLoad, wizardState]);

	// Clear cycle loads when cycle changes (only on actual change, not initial mount)
	useEffect(() => {
		const cycleId = wizardState.cycle?._id?.$oid || wizardState.cycle?._id || wizardState.cycle?.id;

		// Only clear if the cycle ID actually changed (not on first mount)
		if (previousCycleIdRef.current !== null && previousCycleIdRef.current !== cycleId) {
			dispatch({ type: 'CLEAR_CYCLE_LOADS' });
			fetchedCycleRef.current = null;
		}

		previousCycleIdRef.current = cycleId;
	}, [wizardState.cycle?._id?.$oid, dispatch]);

	// Clear errors when component unmounts or wizard state changes
	useEffect(() => {
		return () => {
			dispatch({ type: 'CLEAR_SELF_ERRORS' });
		};
	}, [dispatch]);

	// Parse CSV and get preview data
	const parseCSVPreview = (file) => {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const text = e.target.result;
				const lines = text.split('\n').filter(line => line.trim());
				const headers = lines[0] ? lines[0].split(',').map(h => h.trim()) : [];
				const rows = lines.slice(1, 21).map(line =>
					line.split(',').map(cell => cell.trim())
				);
				resolve({ headers, rows, totalRows: lines.length - 1 });
			};
			reader.onerror = () => resolve(null);
			reader.readAsText(file);
		});
	};

	// Handle file selection without uploading
	const handleFileChange = async (info) => {
		const { file } = info;
		if (file.status === 'removed') {
			setSelectedFile(null);
			setFilePreview(null);
			return;
		}

		// Store the actual file object
		const fileObj = file.originFileObj || file;
		setSelectedFile(fileObj);

		// Generate preview for CSV files
		if (fileObj && (fileObj.name.endsWith('.csv') || fileObj.type === 'text/csv')) {
			const preview = await parseCSVPreview(fileObj);
			setFilePreview(preview);
		}
	};

	// Add file to uploads table and perform upload
	const handleAddUpload = async () => {
		if (!selectedFile || !fileName.trim() || !fileDescription.trim()) {
			message.error('Por favor completa todos los campos y selecciona un archivo');
			return;
		}
		
		// First get time estimation
		setIsEstimating(true);
		
		const estimationBody = {
			organization: wizardState.organization._id?.$oid || wizardState.organization._id || wizardState.organization.id,
			workspace: wizardState.workspace._id?.$oid || wizardState.workspace._id || wizardState.workspace.id,
			module: wizardState.module._id?.$oid || wizardState.module._id || wizardState.module.id,
			type: getLoadTypeValue(uploadType),
			file: selectedFile
		};
		
		// Show estimation modal immediately
		setEstimationModal(true);
		
		try {
			const estimation = await estimate_file_time(estimationBody);
			setTimeEstimation(estimation);
			setIsEstimating(false);
		} catch (err) {
			// If estimation fails, continue with upload anyway
			console.warn('No se pudo obtener la estimación de tiempo, procediendo con la carga');
			setIsEstimating(false);
			proceedWithUpload();
		}
	};
	
	// Proceed with actual upload after confirmation
	const proceedWithUpload = async () => {
		setEstimationModal(false);

		const newUpload = {
			key: Date.now(),
			name: fileName,
			description: fileDescription,
			status: 'Preparado',
			date: new Date().toLocaleString(),
			file: selectedFile.name,
			fileObject: selectedFile, // Store the actual file object
			loadType: uploadType,
			cycle: wizardState.cycle,
			organization: wizardState.organization,
			workspace: wizardState.workspace,
			module: wizardState.module,
			// Store the load type for the API call
			loadTypeValue: getLoadTypeValue(uploadType)
		};

		// Clear previous errors before starting upload
		dispatch({ type: 'CLEAR_SELF_ERRORS' });
		setIsUploading(true);

		try {
			const cycleId = newUpload.cycle?._id?.$oid || newUpload.cycle?._id || newUpload.cycle?.id;
			const loadId = wizardState.load_id;

			const body = {
				organization: newUpload.organization?.id || '',
				workspace: newUpload.workspace?.id || '',
				module: newUpload.module?.id || '',
				name: newUpload.name,
				description: newUpload.description,
				file: newUpload.fileObject,
				type: getLoadTypeValue(newUpload.loadType), // Based on load_types
			};

			if (newUpload.loadType === 'Inicial') {
				body.load = loadId || '';
			} else {
				// For additional loads, use cycle as load reference
				body.cycle = cycleId || '';
			}

			// Debug log to verify cycle ID is being sent
			// console.log('Upload body:', body);
			// console.log('Cycle object:', newUpload.cycle);
			// console.log('Cycle ID being sent:', cycleId);

			// Use the existing upload_file action and wait for result
			const uploadMessageApi = {
				loading: (msg, duration) => message.loading(msg, duration),
				success: (msg, duration, responseData) => {
					message.success(msg, duration);
					setIsUploading(false);

					// Extract file_id from response
					const fileId = responseData?.file;
					const updatedUpload = { ...newUpload, file_id: fileId };

					// Add to uploads table
					setUploads([...uploads, updatedUpload]);
					setFileName('');
					setFileDescription('');
					setSelectedFile(null);
					setFilePreview(null);

					// Update wizard state with successful upload and file_id
					const updatedWizardState = {
						...wizardState,
						uploads: [...uploads, updatedUpload],
						loadName: loadName,
						loadDescription: loadDescription,
						uploadCompleted: true,
						lastFileId: fileId // Store the file ID for status page
					};
					localStorage.setItem('wizardState', JSON.stringify(updatedWizardState));
					setWizardState(updatedWizardState); // Update local state too
				},
				error: () => {
					setIsUploading(false);
					// The error will be displayed in the UI automatically
				}
			};

			dispatch(upload_file(uploadMessageApi, body));

		} catch (error) {
			console.error('Error during upload:', error);
			setIsUploading(false);
			message.error('Error inesperado durante la carga');
		}
	};

	const handleContinue = () => {
		if (uploads.length === 0) {
			message.error('Debes agregar al menos un archivo para continuar');
			return;
		}

		// Navigate to status page with appropriate parameters
		if (uploadType === 'Inicial' && wizardState.load_id) {
			// For initial loads, navigate with load_id
			navigate(`/wizard/status/load=${wizardState.load_id}`);
		} else if (uploads.length > 0 && uploads[0].file_id) {
			// For other load types, navigate with file_id
			navigate(`/wizard/status/file=${uploads[0].file_id}`);
		} else {
			// Fallback to generic status page
			navigate('/wizard/status');
		}
	};

	const handleBack = () => {
		navigate('/wizard/cycles');
	};

	const handleRestart = () => {
		Modal.confirm({
			title: '¿Estás seguro de que quieres reiniciar el proceso completo?',
			icon: <ExclamationCircleOutlined />,
			content: 'Se perderá todo el progreso actual incluyendo la selección de organización, esquemas, ciclo y archivos agregados. Esta acción no se puede deshacer.',
			okText: 'Sí, reiniciar todo',
			okType: 'danger',
			cancelText: 'Cancelar',
			centered: true,
			width: 500,
			onOk() {
				localStorage.removeItem('wizardState');
				navigate('/wizard/start');
			},
		});
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		onChange: handleFileChange,
		beforeUpload: (file) => {
			const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
			if (!isCSV) {
				message.error('Solo puedes subir archivos CSV');
				return false;
			}
			// Return false to prevent automatic upload
			return false;
		},
		showUploadList: false, // Hide the default upload list since we show our own preview
		maxCount: 1, // Only allow one file at a time
		fileList: [], // Keep fileList empty to prevent any file references
		onRemove: () => {
			setSelectedFile(null);
			setFilePreview(null);
		}
	};

	// Restrict adding multiple files for additional loads, coordinates and restructure
	const canAddMoreFiles = uploadType === 'Inicial' || uploads.length === 0;

	const columns = [
		{
			title: 'NOMBRE',
			dataIndex: 'name',
			key: 'name'
		},
		{
			title: 'DESCRIPCIÓN',
			dataIndex: 'description',
			key: 'description'
		},
		// {
		// 	title: 'ESTADO',
		// 	dataIndex: 'status',
		// 	key: 'status',
		// 	render: (status) => (
		// 		<Tag color={status === 'Preparado' ? 'blue' : get_load_status_color(1)}>
		// 			{status}
		// 		</Tag>
		// 	)
		// },
		{
			title: 'FECHA',
			dataIndex: 'date',
			key: 'date'
		},
		{
			title: 'ARCHIVO',
			dataIndex: 'file',
			key: 'file'
		},
		{
			title: '',
			key: 'actions',
			width: 100,
			render: (_, record) => (
				<Space>
					<Tooltip title="Editar">
						<Button
							type="primary"
							shape="circle"
							size="large"
							icon={<i className="bi bi-pencil-fill" />}
						/>
					</Tooltip>
					<Tooltip title="Eliminar">
						<Button
							danger
							shape="circle"
							size="large"
							icon={<span className="material-symbols-outlined">delete</span>}
							onClick={() => {
								setUploads(uploads.filter(u => u.key !== record.key));
							}}
							style={{ 
								backgroundColor: '#ff4d4f',
								borderColor: '#ff4d4f',
								color: 'white'
							}}
						/>
					</Tooltip>
				</Space>
			)
		}
	];

	// Check if user can continue based on uploads and load requirements
	const canContinue = () => {
		if (uploads.length === 0) return false;

		// For initial loads on existing cycles, we need to have found the associated load
		if (uploadType === 'Inicial' && wizardState.loadType === 'additional') {
			return cycleLoad !== null && !cycleLoadError;
		}

		return true;
	};

	return (
		<div className="wizard-container">
			<WizardProgress currentStep={3} />

			<div className="content-container" style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
				{/* Header crítico con información del contexto */}
				<Card
					style={{
						marginBottom: '30px',
						borderRadius: '8px',
						border: '2px solid #3e619b',
						backgroundColor: '#f0f6ff'
					}}
				>
					<Row justify="space-between" align="middle">
						<Col>
							<h1 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0, color: '#3e619b' }}>
								PASO 3: CARGAR ARCHIVOS
							</h1>
						</Col>
					</Row>
					<Row style={{ marginTop: '20px' }}>
						<Col span={24}>
							<div style={{
								padding: '16px',
								backgroundColor: 'white',
								borderRadius: '6px',
								border: '1px solid #d9d9d9'
							}}>
								<Typography.Title level={4} style={{ margin: '0 0 12px 0', color: '#d32f2f' }}>
									<WarningFilled style={{ color: "#FAAD14" }} /> INFORMACIÓN CRÍTICA - Verificar antes de continuar
								</Typography.Title>
								<Row gutter={[16, 8]}>
									<Col xs={24} sm={8}>
										<strong style={{ color: '#3e619b' }}>Organización:</strong><br />
										<span style={{ fontSize: '16px', fontWeight: '600' }}>
											{wizardState.organization?.name || 'Sin información'}
										</span>
									</Col>
									<Col xs={24} sm={8}>
										<strong style={{ color: '#3e619b' }}>Espacio de Trabajo:</strong><br />
										<span style={{ fontSize: '16px', fontWeight: '600' }}>
											{wizardState.workspace?.name || 'Sin información'}
										</span>
									</Col>
									<Col xs={24} sm={8}>
										<strong style={{ color: '#3e619b' }}>Módulo:</strong><br />
										<span style={{ fontSize: '16px', fontWeight: '600' }}>
											{wizardState.module?.name || 'Sin información'}
										</span>
									</Col>
								</Row>
								<Row style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }} gutter={[16, 16]}>
									<Col xs={24} sm={8}>
										<strong style={{ color: '#3e619b' }}>Ciclo Seleccionado:</strong><br />
										<span style={{ fontSize: '16px', fontWeight: '600' }}>
											{wizardState.cycle?.name || 'No seleccionado'}
										</span>
									</Col>
									<Col xs={24} sm={8}>
										<strong style={{ color: '#3e619b' }}>Tipo de Carga:</strong><br />
										<span style={{ fontSize: '16px', fontWeight: '600' }}>
											{uploadType}
										</span>
									</Col>

									{/* Mostrar Load Asociada solo para ciclos con status 1 */}
									{wizardState.cycleSelectionType === 1 && uploadType === 'Inicial' && (
										<Col xs={24} sm={8}>
											<strong style={{ color: '#3e619b' }}>Load Asociada:</strong><br />
											{cycle_loads_loading && (
												<span style={{ fontSize: '16px', color: '#1890ff' }}>
													Buscando load asociada...
												</span>
											)}
											{cycleLoadError && (
												<span style={{ fontSize: '16px', color: '#ff4d4f' }}>
													{cycleLoadError}
												</span>
											)}
											{cycleLoad && (
												<span style={{ fontSize: '16px', fontWeight: '600' }}>
													{cycleLoad.name}
												</span>
											)}
										</Col>
									)}
								</Row>
								<Row style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }} justify="space-between" align="middle">
									<Col span={16}>
										<div style={{
											padding: '8px 12px',
											backgroundColor: '#fff3cd',
											borderRadius: '4px',
											fontSize: '14px',
											color: '#856404'
										}}>
											<BulbFilled style={{ color: "#FAAD14" }} /> Si esta información no es correcta, puedes reiniciar el proceso completo.
										</div>
									</Col>
									<Col span={8} style={{ textAlign: 'right' }}>
										<Button
											danger
											size="small"
											icon={<i className="bi bi-arrow-clockwise" />}
											onClick={handleRestart}
											style={{ 
												fontWeight: '600',
												backgroundColor: '#ff4d4f',
												borderColor: '#ff4d4f',
												color: 'white'
											}}
										>
											REINICIAR PROCESO
										</Button>
									</Col>
								</Row>
							</div>
						</Col>
					</Row>
				</Card>

				<Row gutter={[40, 40]}>
					{/* Load Types Legend - Left Column */}
					<Col xs={24} lg={12}>
						<Card style={{ height: '100%', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
						<div style={{ marginBottom: '16px' }}>
							<Typography.Title level={5} style={{ margin: 0, color: '#495057' }}>
							<InfoCircleFilled style={{ color: '#1890ff' }}/> Tipos de Carga (Estado del Ciclo: {get_cycle_status_desc(wizardState.cycleSelectionType)})
						</Typography.Title>
						{wizardState.cycleSelectionType && (
							<div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
								{wizardState.cycleSelectionType === 1 
									? `Para ciclos con estado ${get_cycle_status_desc(1)} solo está disponible la carga inicial`
									: wizardState.cycleSelectionType === 3
										? `Para ciclos con estado ${get_cycle_status_desc(3)} no está disponible la carga inicial`
										: `Para ciclos con estado ${get_cycle_status_desc(wizardState.cycleSelectionType)} están disponibles todos los tipos excepto inicial`
								}
							</div>
						)}
					</div>
					<Row gutter={[16, 12]}>
						<Col xs={24}>
							<div style={{ 
								padding: '12px', 
								backgroundColor: availableLoadTypes.includes('Inicial') ? 'white' : '#f5f5f5', 
								borderRadius: '6px', 
								border: availableLoadTypes.includes('Inicial') ? '2px solid #007bff' : '1px solid #e9ecef',
								opacity: availableLoadTypes.includes('Inicial') ? 1 : 0.6
							}}>
								<div style={{ 
									fontWeight: '600', 
									color: availableLoadTypes.includes('Inicial') ? '#007bff' : '#adb5bd', 
									marginBottom: '4px' 
								}}>
									Inicial {availableLoadTypes.includes('Inicial') ? '✓' : '✗'}
								</div>
								<div style={{ fontSize: '12px', color: '#6c757d' }}>
									Habilita un ciclo después de subir archivos.
								</div>
							</div>
						</Col>
						{/* <Col xs={24} sm={12} md={6}>
							<div style={{ 
								padding: '12px', 
								backgroundColor: availableLoadTypes.includes('Reestructura') ? 'white' : '#f5f5f5', 
								borderRadius: '6px', 
								border: availableLoadTypes.includes('Reestructura') ? '2px solid #fd7e14' : '1px solid #e9ecef',
								opacity: availableLoadTypes.includes('Reestructura') ? 1 : 0.6
							}}>
								<div style={{ 
									fontWeight: '600', 
									color: availableLoadTypes.includes('Reestructura') ? '#fd7e14' : '#adb5bd', 
									marginBottom: '4px' 
								}}>
									Reestructura {availableLoadTypes.includes('Reestructura') ? '✓' : '✗'}
								</div>
								<div style={{ fontSize: '12px', color: '#6c757d' }}>
									Modifica rutas existentes reubicando tiendas.
								</div>
							</div>
						</Col> */}
						<Col xs={24}>
							<div style={{
								padding: '12px',
								backgroundColor: availableLoadTypes.includes('Reestructura') ? 'white' : '#f5f5f5', 
								borderRadius: '6px', 
								border: availableLoadTypes.includes('Reestructura') ? '2px solid #28a745' : '1px solid #e9ecef',
								opacity: availableLoadTypes.includes('Reestructura') ? 1 : 0.6
							}}>
								<div style={{ 
									fontWeight: '600', 
									color: availableLoadTypes.includes('Reestructura') ? '#28a745' : '#adb5bd', 
									marginBottom: '4px' 
								}}>
									Reestructura {availableLoadTypes.includes('Reestructura') ? '✓' : '✗'}
								</div>
								<div style={{ fontSize: '12px', color: '#6c757d' }}>
									Crea nuevas áreas y tiendas, y las asigna a las rutas existentes.
								</div>
							</div>
						</Col>
						<Col xs={24}>
							<div style={{
								padding: '12px',
								backgroundColor: availableLoadTypes.includes('Coordenadas') ? 'white' : '#f5f5f5', 
								borderRadius: '6px', 
								border: availableLoadTypes.includes('Coordenadas') ? '2px solid #6f42c1' : '1px solid #e9ecef',
								opacity: availableLoadTypes.includes('Coordenadas') ? 1 : 0.6
							}}>
								<div style={{ 
									fontWeight: '600', 
									color: availableLoadTypes.includes('Coordenadas') ? '#6f42c1' : '#adb5bd', 
									marginBottom: '4px' 
								}}>
									Coordenadas {availableLoadTypes.includes('Coordenadas') ? '✓' : '✗'}
								</div>
								<div style={{ fontSize: '12px', color: '#6c757d' }}>
									Actualiza únicamente las coordenadas de las tiendas registradas.
								</div>
							</div>
						</Col>
					</Row>
					<Typography.Paragraph style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
						<blockquote>
							<strong>Tiendas: </strong>clientes o puntos de venta (PDVs).
						</blockquote>
					</Typography.Paragraph>
					</Card>
				</Col>

				{/* Form - Right Column */}
				<Col xs={24} lg={12}>
					<Card style={{ height: '100%' }}>
						<div style={{ marginBottom: '20px' }}>
							<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
								Tipo de carga:
							</label>
							<Select
								value={uploadType}
								onChange={setUploadType}
								style={{ width: '100%' }}
								disabled={isLoadTypeDisabled}
							>
								{availableLoadTypes.includes('Inicial') && <Option value="Inicial">Inicial</Option>}
								{availableLoadTypes.includes('Reestructura') && <Option value="Reestructura">Reestructura</Option>}
								{availableLoadTypes.includes('Adicional') && <Option value="Adicional">Adicional</Option>}
								{availableLoadTypes.includes('Coordenadas') && <Option value="Coordenadas">Coordenadas</Option>}
							</Select>
						</div>

						<div style={{ marginBottom: '20px' }}>
							<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
								Nombre:
							</label>
							<Input
								value={fileName}
								onChange={(e) => setFileName(e.target.value)}
								placeholder="Ingresa el nombre del archivo"
							/>
						</div>

						<div style={{ marginBottom: '20px' }}>
							<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
								Descripción:
							</label>
							<Input.TextArea
								rows={5}
								value={fileDescription}
								onChange={(e) => setFileDescription(e.target.value)}
								placeholder="Ingresa la descripción del archivo"
							/>
						</div>
					</Card>
				</Col>
			</Row>

				{/* Show upload section only if no files are added for additional loads and coordinates */}
				{!(!canAddMoreFiles && uploads.length > 0) && (
					<Card style={{ marginTop: '30px', marginBottom: '30px' }}>
						{!filePreview ? (
							<Dragger {...uploadProps} style={{ padding: '40px' }}>
								<p className="ant-upload-drag-icon">
									<InboxOutlined style={{ fontSize: '48px', color: '#3e619b' }} />
								</p>
								<p style={{ fontSize: '18px', fontWeight: '500', margin: '20px 0 10px' }}>
									Haga clic o arrastre un archivo a esta área para cargarlo
								</p>
								<p style={{ color: '#999', margin: 0 }}>
									Por favor, asegúrata de que el archivo que adjuntas sea de tipo .csv
								</p>
							</Dragger>
						) : (
							/* File Preview Section */
							<div style={{ padding: '20px' }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
									<Typography.Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
										<FileTextOutlined style={{ color: '#3e619b' }} />
										{selectedFile?.name}
									</Typography.Title>
									<Button
										size="small"
										danger
										icon={<i className="bi bi-x-circle" />}
										onClick={() => {
											setSelectedFile(null);
											setFilePreview(null);
										}}
										style={{ 
											backgroundColor: '#ff4d4f',
											borderColor: '#ff4d4f',
											color: 'white'
										}}
									>
										Eliminar archivo
									</Button>
								</div>
								<div style={{ overflowX: 'auto', maxHeight: '443px', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
									<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
										<thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
											<tr>
												{filePreview.headers.map((header, index) => (
													<th key={index} style={{
														border: '1px solid #d9d9d9',
														padding: '8px',
														backgroundColor: '#fafafa',
														fontWeight: 'bold',
														textAlign: 'left'
													}}>
														{header}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{filePreview.rows.map((row, rowIndex) => (
												<tr key={rowIndex}>
													{row.map((cell, cellIndex) => (
														<td key={cellIndex} style={{
															border: '1px solid #d9d9d9',
															padding: '8px',
															maxWidth: '150px',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap'
														}}>
															{cell}
														</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								</div>
								{filePreview.totalRows > 20 && (
									<p style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
										Mostrando las primeras 20 filas de {filePreview.totalRows.toLocaleString(navigator.language)} filas totales.
									</p>
								)}
							</div>
						)}

						<div style={{ textAlign: 'center', marginTop: '20px' }}>
							<Button
								type="primary"
								size="large"
								onClick={handleAddUpload}
								disabled={!selectedFile || !fileName.trim() || !fileDescription.trim() || !canAddMoreFiles || isUploading || 
									(uploadType === 'Inicial' && wizardState.loadType === 'additional' && (cycleLoadError || !cycleLoad))}
								loading={isUploading}
								style={{
									height: '50px',
									fontSize: '14px',
									minWidth: '200px',
									backgroundColor: (!selectedFile || !fileName.trim() || !fileDescription.trim() || !canAddMoreFiles || isUploading || 
										(uploadType === 'Inicial' && wizardState.loadType === 'additional' && (cycleLoadError || !cycleLoad))) ? '#d9d9d9' : '#3e619b',
									borderColor: (!selectedFile || !fileName.trim() || !fileDescription.trim() || !canAddMoreFiles || isUploading || 
										(uploadType === 'Inicial' && wizardState.loadType === 'additional' && (cycleLoadError || !cycleLoad))) ? '#d9d9d9' : '#3e619b'
								}}
							>
								{isUploading ? 'CARGANDO...' : 
								 (uploadType === 'Inicial' && wizardState.loadType === 'additional' && (cycleLoadError || !cycleLoad)) ? 'LOAD NO ENCONTRADA' :
								 (!canAddMoreFiles ? 'MÁXIMO 1 ARCHIVO' : 'AGREGAR ARCHIVO')}
							</Button>
						</div>
					</Card>
				)}

				{/* Message for single-file load types when file is already added */}
				{!canAddMoreFiles && uploads.length > 0 && (
					<Card style={{ marginTop: '30px', marginBottom: '30px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
						<div style={{ textAlign: 'center', padding: '40px 20px' }}>
							<div style={{ fontSize: '48px', color: '#52c41a', marginBottom: '20px' }}>
								<CheckCircleFilled />
							</div>
							<Typography.Title level={3} style={{ color: '#52c41a', marginBottom: '16px' }}>
								Archivo Agregado Exitosamente
							</Typography.Title>
							<Typography.Text style={{ fontSize: '16px', color: '#389e0d' }}>
								Para este tipo de carga solo se permite un archivo. <br />
								Procede al siguiente paso para revisar el estado de carga de tu archivo.
							</Typography.Text>
						</div>
					</Card>
				)}

				{/* Error display section - replicating LoadFiles.jsx logic */}
				{self_errors.hasOwnProperty('upload_file') &&
					<Card style={{ marginBottom: '30px', borderRadius: '8px' }}>
						<Alert
							action={
       					<Space>
									<Button 
										size='small' 
										danger 
										onClick={() => setViewErrorDetails(!viewErrorDetails)}
									>
										{t('loadStatus.statusTable.actionButtons.details') || 'View Details'}
									</Button>
									<Button
										size='small'
										style={{ 
											backgroundColor: '#ff4d4f',
											borderColor: '#ff4d4f',
											color: 'white'
										}}
										onClick={() => {
											dispatch({ type: 'CLEAR_SELF_ERRORS' });
											setSelectedFile(null);
											setFilePreview(null);
											setUploads([]);
											setFileName('');
											setFileDescription('');
											setViewErrorDetails(false);
											setIsUploading(false);
										}}
									>
										Descartar y comenzar de nuevo
									</Button>
								</Space>
							}
							message={t('loadFile.msg.error') || 'Error loading file, check the information and try again.'}
							description={viewErrorDetails ? <pre >{JSON.stringify(self_errors.upload_file.msg, null, 2)}</pre> : <p hidden>Error</p>}
							type='error'
							showIcon
						/>
					</Card>
				}

				{uploads.length > 0 && uploadType === 'Inicial' && (
					<Card style={{ marginBottom: '30px' }}>
						<Table
							columns={columns}
							dataSource={uploads}
							pagination={false}
							scroll={{ x: 800 }}
						/>
					</Card>
				)}

				<Row justify="space-between" style={{ marginTop: '40px' }}>
					<Col>
						{/* Hide back button for new cycles to prevent user from changing cycle selection */}
						{wizardState.loadType === 'additional' && (
							<Button
								size="large"
								onClick={handleBack}
								style={{ height: '50px', fontSize: '16px', minWidth: '120px' }}
							>
								← REGRESAR
							</Button>
						)}
					</Col>
					<Col>
						<Button
							type="primary"
							size="large"
							onClick={handleContinue}
							disabled={!canContinue()}
							style={{
								height: '50px',
								fontSize: '16px',
								minWidth: '150px',
								backgroundColor: canContinue() ? '#3e619b' : '#d9d9d9',
								borderColor: canContinue() ? '#3e619b' : '#d9d9d9'
							}}
						>
							CONTINUAR →
						</Button>
					</Col>
				</Row>
			</div>
			
			{/* Time Estimation Modal */}
			<Modal
				title="Estimación de Tiempo de Procesamiento"
				open={estimationModal}
				onOk={proceedWithUpload}
				onCancel={() => {
					setEstimationModal(false);
					setIsEstimating(false);
				}}
				okText="Continuar con la carga"
				cancelText="Cancelar"
				width={500}
				centered
				okButtonProps={{ disabled: isEstimating }}
			>
				{isEstimating ? (
					<div style={{ textAlign: 'center', padding: '40px 20px' }}>
						<Spin size="large" tip="Analizando archivo y estimando tiempo de procesamiento...">
							<div style={{
								padding: 50,
								background: 'rgba(0, 0, 0, 0.05)',
								borderRadius: 4,
							}} />
						</Spin>
					</div>
				) : timeEstimation ? (
					<div style={{ padding: '24px 8px 16px 8px' }}>
						{/* Información del archivo */}
						<div style={{ marginBottom: 24 }}>
							<div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
								<InfoCircleFilled style={{ color: '#1890ff', marginRight: 12, fontSize: '24px' }} />
								<Typography.Title level={5} style={{ margin: 0, color: '#262626' }}>Información del archivo</Typography.Title>
							</div>
							<div style={{ paddingLeft: 36 }}>
								<p style={{ margin: '6px 0', color: '#595959' }}><strong>Archivo:</strong> {selectedFile?.name || timeEstimation.fileName}</p>
								<p style={{ margin: '6px 0', color: '#595959' }}><strong>Tamaño:</strong> {(timeEstimation.fileSize / 1024 / 1024).toFixed(2)} MB</p>
								<p style={{ margin: '6px 0', color: '#595959' }}><strong>Filas:</strong> {timeEstimation.rowCount.toLocaleString(navigator.language)}</p>
							</div>
						</div>
						
						<Divider style={{ margin: '20px 0' }} />
						
						{/* Tiempo estimado de procesamiento */}
						<div style={{ marginBottom: 24 }}>
							<div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
								<ClockCircleFilled style={{ color: '#FAAD14', marginRight: 12, fontSize: '24px' }} />
								<Typography.Title level={5} style={{ margin: 0, color: '#262626' }}>Tiempo estimado de procesamiento</Typography.Title>
							</div>
							<div style={{ paddingLeft: 36 }}>
								<p style={{ margin: '6px 0', color: '#595959' }}><strong>Validación:</strong> ~{Math.ceil(timeEstimation.estimatedValidationSeconds / 60)} minutos</p>
								<p style={{ margin: '6px 0', color: '#595959' }}><strong>Procesamiento:</strong> ~{Math.ceil(timeEstimation.estimatedProcessingSeconds / 60)} minutos</p>
								<p style={{ fontSize: '16px', fontWeight: 'bold', margin: '12px 0 6px 0', color: '#262626' }}>
									<strong>Tiempo total estimado:</strong> ~{Math.ceil(timeEstimation.estimatedTotalSeconds / 60)} minutos
								</p>
							</div>
						</div>
						
						<Divider style={{ margin: '20px 0' }} />
						
						{/* Nivel de confianza */}
						<div style={{ marginBottom: 24 }}>
							<div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
								<CheckCircleFilled style={{ 
									color: timeEstimation.confidence === 'high' ? '#52c41a' : '#1890ff', 
									marginRight: 12, 
									fontSize: '24px' 
								}} />
								<Typography.Title level={5} style={{ margin: 0, color: '#262626' }}>
									Nivel de confianza: {timeEstimation.confidence === 'high' ? 'Alto' : 
									timeEstimation.confidence === 'medium' ? 'Medio' : 'Bajo'}
								</Typography.Title>
							</div>
							<div style={{ paddingLeft: 36 }}>
								<p style={{ margin: '6px 0', color: '#595959' }}>
									{timeEstimation.confidence === 'high' 
										? `Basado en ${timeEstimation.sampleCount} archivos similares procesados anteriormente`
										: timeEstimation.confidence === 'medium'
										? `Basado en ${timeEstimation.sampleCount} archivos procesados`
										: 'Estimación basada en valores predeterminados'}
								</p>
							</div>
						</div>
						
						<div style={{ marginTop: 28, padding: 12, backgroundColor: '#f0f0f0', borderRadius: 4 }}>
							<p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
								<strong>Nota:</strong> El tiempo real puede variar dependiendo de la carga del sistema y la complejidad de las validaciones.
							</p>
						</div>
					</div>
				) : null}
			</Modal>
		</div>
	);
};

export default WizardUpload;